import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";

const inputCls =
  "w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-[#FBF9F5] placeholder-[#94A3B8]/50 outline-none transition-colors duration-300 focus:border-[#C5A059]/70";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] text-[#94A3B8] mb-2 font-semibold";

export default function EnquiryForm({ type, title, intro, fields = [], dark = true }) {
  const [values, setValues] = useState({});
  const [consent, setConsent] = useState(false);
  const [honey, setHoney] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const location = useLocation();

  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      await api.post("/enquiries", {
        type,
        name: values.name || "",
        phone: values.phone || "",
        email: values.email || null,
        message: values.message || "",
        consent,
        honey,
        page: location.pathname + location.search,
        payload: Object.fromEntries(
          Object.entries(values).filter(([k]) => !["name", "phone", "email", "message"].includes(k))
        ),
      });
      setStatus("success");
      toast.success("Enquiry received. Our team will contact you.");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us directly.");
      toast.error("Submission failed. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid={`enquiry-${type}-success`} className="border border-[#C5A059]/40 bg-[#0B1320] p-10 text-center">
        <p className="font-serif text-3xl text-[#FBF9F5]">Thank you, {values.name?.split(" ")[0] || "there"}.</p>
        <p className="text-sm text-[#94A3B8] mt-4 leading-relaxed max-w-md mx-auto">
          Your enquiry has been recorded and routed to the right team. We will contact you on{" "}
          <span className="text-[#C5A059]">{values.phone}</span>.
        </p>
        <button
          data-testid={`enquiry-${type}-again-btn`}
          onClick={() => {
            setStatus("idle");
            setValues({});
            setConsent(false);
          }}
          className="mt-8 text-[11px] uppercase tracking-[0.2em] text-[#C5A059] border border-[#C5A059]/50 px-6 py-3 hover:bg-[#C5A059] hover:text-[#060A12] transition-colors duration-300"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form data-testid={`enquiry-${type}-form`} onSubmit={submit} className="space-y-6">
      {(title || intro) && (
        <div className="mb-2">
          {title && <h3 className="font-serif text-2xl sm:text-3xl text-[#FBF9F5]">{title}</h3>}
          {intro && <p className="text-sm text-[#94A3B8] mt-3 leading-relaxed">{intro}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={labelCls} htmlFor={`${type}-name`}>Full name *</label>
          <input id={`${type}-name`} data-testid={`enquiry-${type}-name`} required className={inputCls}
            value={values.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
        </div>
        <div>
          <label className={labelCls} htmlFor={`${type}-phone`}>Mobile number *</label>
          <input id={`${type}-phone`} data-testid={`enquiry-${type}-phone`} required type="tel" className={inputCls}
            value={values.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+91" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor={`${type}-email`}>Email</label>
          <input id={`${type}-email`} data-testid={`enquiry-${type}-email`} type="email" className={inputCls}
            value={values.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
        </div>

        {fields.map((f) => (
          <div key={f.name} className={f.wide ? "sm:col-span-2" : ""}>
            <label className={labelCls} htmlFor={`${type}-${f.name}`}>
              {f.label} {f.required ? "*" : ""}
            </label>
            {f.type === "select" ? (
              <select id={`${type}-${f.name}`} data-testid={`enquiry-${type}-${f.name}`} required={f.required}
                className={`${inputCls} bg-[#0B1320]`} value={values[f.name] || ""}
                onChange={(e) => set(f.name, e.target.value)}>
                <option value="">Select…</option>
                {f.options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : f.type === "textarea" ? (
              <textarea id={`${type}-${f.name}`} data-testid={`enquiry-${type}-${f.name}`} rows={4}
                required={f.required} className={inputCls} value={values[f.name] || ""}
                onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder || ""} />
            ) : (
              <input id={`${type}-${f.name}`} data-testid={`enquiry-${type}-${f.name}`} type={f.type || "text"}
                required={f.required} className={inputCls} value={values[f.name] || ""}
                onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder || ""} />
            )}
          </div>
        ))}

        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor={`${type}-message`}>Message</label>
          <textarea id={`${type}-message`} data-testid={`enquiry-${type}-message`} rows={3} className={inputCls}
            value={values.message || ""} onChange={(e) => set("message", e.target.value)}
            placeholder="Anything else we should know" />
        </div>
      </div>

      <input type="text" name="company_website" value={honey} onChange={(e) => setHoney(e.target.value)}
        className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <label className="flex items-start gap-3 cursor-pointer" data-testid={`enquiry-${type}-consent`}>
        <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 accent-[#C5A059]" />
        <span className="text-xs text-[#94A3B8] leading-relaxed">
          I consent to RK Group contacting me about this enquiry by phone, WhatsApp, or email. *
        </span>
      </label>

      {status === "error" && (
        <p data-testid={`enquiry-${type}-error`} className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">
          {error}
        </p>
      )}

      <button type="submit" data-testid={`enquiry-${type}-submit`} disabled={status === "submitting"}
        className="w-full sm:w-auto text-[11px] uppercase tracking-[0.22em] font-semibold bg-[#C5A059] text-[#060A12] px-10 py-4 hover:bg-[#d6b678] transition-colors duration-300 disabled:opacity-50">
        {status === "submitting" ? "Sending…" : "Submit Enquiry"}
      </button>
    </form>
  );
}
